import { Check, ChevronDown, ChevronRight, Clock, Copy } from "lucide-react";
import { useState } from "react";
import type { SkillCheckJobStepDetail, StepName } from "../../types/admin.types";
import { Badge } from "../ui/Badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";

type LLMInputOutputViewerProps = {
  steps: SkillCheckJobStepDetail[];
};

const stepLabels: Record<StepName, { title: string; description: string }> = {
  candidate_extraction: {
    title: "候補者情報抽出",
    description: "HTMLから候補者の基本情報（名前、スキル、経歴等）を抽出",
  },
  skill_matching: {
    title: "スキルマッチング分析",
    description: "候補者スキルと求人要件のマッチング度を分析",
  },
  criteria_evaluation: {
    title: "カスタム判定評価",
    description: "カスタム判定項目に基づく評価",
  },
};

function getStatusColorClass(status: string): string {
  if (status === "completed") {
    return "bg-green-100 text-green-700";
  }
  if (status === "failed") {
    return "bg-red-100 text-red-700";
  }
  return "bg-yellow-100 text-yellow-700";
}

function getStatusBadgeVariant(status: string): "success" | "error" | "warning" {
  if (status === "completed") {
    return "success";
  }
  if (status === "failed") {
    return "error";
  }
  return "warning";
}

type StepItemProps = {
  step: SkillCheckJobStepDetail;
  isSelected: boolean;
  onSelect: () => void;
};

function OutputDataDisplay({ step }: { step: SkillCheckJobStepDetail }) {
  if (step.outputData) {
    return <JsonViewer data={step.outputData} />;
  }

  if (step.status === "failed") {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="font-medium text-red-700">処理失敗</p>
        <p className="mt-1 text-red-600 text-sm">{step.errorMessage ?? "エラーメッセージなし"}</p>
      </div>
    );
  }

  return <p className="text-gray-500 text-sm">出力データなし</p>;
}

function StepItem({ step, isSelected, onSelect }: StepItemProps) {
  const label = stepLabels[step.stepName];
  const duration =
    step.completedAt && step.startedAt
      ? (new Date(step.completedAt).getTime() - new Date(step.startedAt).getTime()) / 1000
      : null;

  return (
    <button
      className={`w-full p-4 text-left transition-colors ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"}`}
      onClick={onSelect}
      type="button"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full font-medium text-sm ${getStatusColorClass(step.status)}`}
          >
            {step.stepNumber}
          </span>
          <div>
            <p className="font-medium text-gray-900 text-sm">{label.title}</p>
            <p className="text-gray-500 text-xs">{label.description}</p>
          </div>
        </div>
        <Badge className="text-xs" variant={getStatusBadgeVariant(step.status)}>
          {step.status}
        </Badge>
      </div>
      {duration !== null && (
        <div className="mt-2 flex items-center gap-1 text-gray-500 text-xs">
          <Clock className="h-3 w-3" />
          {duration.toFixed(2)}s
        </div>
      )}
    </button>
  );
}

export function LLMInputOutputViewer({ steps }: LLMInputOutputViewerProps) {
  const [selectedStep, setSelectedStep] = useState<number>(1);

  const currentStep = steps.find((s) => s.stepNumber === selectedStep);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
      {/* ステップ一覧（左側） */}
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">処理ステップ</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-200">
              {steps.map((step) => (
                <StepItem
                  isSelected={step.stepNumber === selectedStep}
                  key={step.id}
                  onSelect={() => setSelectedStep(step.stepNumber)}
                  step={step}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 入出力詳細（右側） */}
      <div className="space-y-6 lg:col-span-3">
        {currentStep ? (
          <>
            {/* 入力データ */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-blue-600">入力データ</span>
                  <Badge variant="info">Input</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {currentStep.inputData ? (
                  <JsonViewer data={currentStep.inputData} />
                ) : (
                  <p className="text-gray-500 text-sm">入力データなし</p>
                )}
              </CardContent>
            </Card>

            {/* 出力データ */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-green-600">出力データ</span>
                  <Badge variant="success">Output</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <OutputDataDisplay step={currentStep} />
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              ステップを選択してください
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// JSONビューアコンポーネント（パフォーマンス最適化済み - PERF-003）
type JsonViewerProps = {
  data: Record<string, unknown>;
};

// JSONデータのサイズを推定（パフォーマンス最適化用）
function estimateJsonSize(data: unknown): number {
  try {
    return JSON.stringify(data).length;
  } catch {
    return 0;
  }
}

// 大きなデータの閾値
const LARGE_DATA_THRESHOLD = 50_000; // 50KB
const MAX_INITIAL_ITEMS = 20; // 初期表示する配列/オブジェクトの最大項目数

function JsonViewer({ data }: JsonViewerProps) {
  const dataSize = estimateJsonSize(data);
  const isLargeData = dataSize > LARGE_DATA_THRESHOLD;

  return (
    <div className="max-h-[600px] overflow-auto rounded-lg bg-gray-900 p-4">
      <div className="mb-2 flex items-center justify-between">
        {isLargeData && (
          <span className="text-amber-400 text-xs">
            大きなデータ ({Math.round(dataSize / 1024)}KB) - 表示を最適化しています
          </span>
        )}
        <div className="ml-auto">
          <CopyButton text={JSON.stringify(data, null, 2)} />
        </div>
      </div>
      <JsonNode data={data} isLargeData={isLargeData} level={0} />
    </div>
  );
}

type JsonNodeProps = {
  data: unknown;
  level: number;
  keyName?: string;
  isLargeData?: boolean;
};

function JsonNode({ data, level, keyName, isLargeData = false }: JsonNodeProps) {
  // 大きなデータの場合は初期展開を制限（PERF-003対応）
  const initialExpandDepth = isLargeData ? 1 : 2;
  const [isExpanded, setIsExpanded] = useState(level < initialExpandDepth);
  // 大きな配列/オブジェクトの表示制限用
  const [showAll, setShowAll] = useState(false);

  const indent = level * 16;

  if (data === null) {
    return (
      <div className="flex items-center gap-2" style={{ marginLeft: indent }}>
        {keyName && <span className="text-purple-400">"{keyName}"</span>}
        {keyName && <span className="text-gray-400">:</span>}
        <span className="text-gray-500">null</span>
      </div>
    );
  }

  if (typeof data === "boolean") {
    return (
      <div className="flex items-center gap-2" style={{ marginLeft: indent }}>
        {keyName && <span className="text-purple-400">"{keyName}"</span>}
        {keyName && <span className="text-gray-400">:</span>}
        <span className="text-yellow-400">{data ? "true" : "false"}</span>
      </div>
    );
  }

  if (typeof data === "number") {
    return (
      <div className="flex items-center gap-2" style={{ marginLeft: indent }}>
        {keyName && <span className="text-purple-400">"{keyName}"</span>}
        {keyName && <span className="text-gray-400">:</span>}
        <span className="text-blue-400">{data}</span>
      </div>
    );
  }

  if (typeof data === "string") {
    // 大きなデータモードでは文字列の切り詰めをより積極的に
    const truncateLength = isLargeData ? 50 : 100;
    const isLongString = data.length > truncateLength;
    return (
      <div className="flex items-start gap-2" style={{ marginLeft: indent }}>
        {keyName && <span className="text-purple-400">"{keyName}"</span>}
        {keyName && <span className="text-gray-400">:</span>}
        <span className="break-all text-green-400">
          "{isLongString ? `${data.slice(0, truncateLength)}...` : data}"
        </span>
        {isLongString && <span className="text-gray-500 text-xs">({data.length}文字)</span>}
      </div>
    );
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return (
        <div className="flex items-center gap-2" style={{ marginLeft: indent }}>
          {keyName && <span className="text-purple-400">"{keyName}"</span>}
          {keyName && <span className="text-gray-400">:</span>}
          <span className="text-gray-400">[]</span>
        </div>
      );
    }

    // 大きな配列の表示制限（PERF-003対応）
    const shouldLimit = isLargeData && data.length > MAX_INITIAL_ITEMS && !showAll;
    const displayItems = shouldLimit ? data.slice(0, MAX_INITIAL_ITEMS) : data;
    const hiddenCount = data.length - MAX_INITIAL_ITEMS;

    return (
      <div style={{ marginLeft: indent }}>
        <button
          className="flex items-center gap-1 text-gray-400 hover:text-gray-200"
          onClick={() => setIsExpanded(!isExpanded)}
          type="button"
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {keyName && <span className="text-purple-400">"{keyName}"</span>}
          {keyName && <span className="mr-1 text-gray-400">:</span>}
          <span>[{data.length}]</span>
        </button>
        {isExpanded && (
          <div className="ml-4">
            {displayItems.map((item, index) => (
              <JsonNode data={item} isLargeData={isLargeData} key={index} level={level + 1} />
            ))}
            {shouldLimit && (
              <button
                className="mt-1 text-blue-400 text-sm hover:text-blue-300"
                onClick={() => setShowAll(true)}
                type="button"
              >
                + 残り {hiddenCount} 件を表示
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (typeof data === "object") {
    const keys = Object.keys(data as object);
    if (keys.length === 0) {
      return (
        <div className="flex items-center gap-2" style={{ marginLeft: indent }}>
          {keyName && <span className="text-purple-400">"{keyName}"</span>}
          {keyName && <span className="text-gray-400">:</span>}
          <span className="text-gray-400">{"{}"}</span>
        </div>
      );
    }

    // 大きなオブジェクトの表示制限（PERF-003対応）
    const shouldLimit = isLargeData && keys.length > MAX_INITIAL_ITEMS && !showAll;
    const displayKeys = shouldLimit ? keys.slice(0, MAX_INITIAL_ITEMS) : keys;
    const hiddenCount = keys.length - MAX_INITIAL_ITEMS;

    return (
      <div style={{ marginLeft: indent }}>
        <button
          className="flex items-center gap-1 text-gray-400 hover:text-gray-200"
          onClick={() => setIsExpanded(!isExpanded)}
          type="button"
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {keyName && <span className="text-purple-400">"{keyName}"</span>}
          {keyName && <span className="mr-1 text-gray-400">:</span>}
          <span>{`{${keys.length}}`}</span>
        </button>
        {isExpanded && (
          <div className="ml-4">
            {displayKeys.map((key) => (
              <JsonNode
                data={(data as Record<string, unknown>)[key]}
                isLargeData={isLargeData}
                key={key}
                keyName={key}
                level={level + 1}
              />
            ))}
            {shouldLimit && (
              <button
                className="mt-1 text-blue-400 text-sm hover:text-blue-300"
                onClick={() => setShowAll(true)}
                type="button"
              >
                + 残り {hiddenCount} 件を表示
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
}

// コピーボタン
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      className="flex items-center gap-1 rounded bg-gray-800 px-2 py-1 text-gray-400 text-xs hover:text-gray-200"
      onClick={handleCopy}
      type="button"
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" />
          コピー済み
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" />
          コピー
        </>
      )}
    </button>
  );
}
